import {
  Box,
  Form,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import {
  CreateFinanceSpendingPolicyDocument,
  CreateFinanceSpendingPolicyMutationVariables,
} from 'generated/resources/graphql'

import TextEditor from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { validateMandatoryFields } from 'modules/finance/lib/newSpendingPoliciesWizard'
import { WizardData, WizardDataField } from 'modules/finance/lib/policy_types'

import IconSelector from '../../SpendingPolicies/Detail/SpendingPolicyForm/IconSelector'

const BasicInfo = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { company } = useCurrentSession()

  const [createSpendingPolicy] = useMutation(CreateFinanceSpendingPolicyDocument, {
    extractorFn: (doc) => doc.financeMutations.createFinanceSpendingPolicySpendingPolicy,
    refetchQueries: [],
  })

  const handleNext = async () => {
    const mandatoryFields: WizardDataField[] = ['icon', 'name']
    const { invalidFields, dataWithErrors } = validateMandatoryFields(mandatoryFields, data)

    if (invalidFields.length) {
      onDataChanged(dataWithErrors)
      return Promise.reject()
    }

    const policyData: CreateFinanceSpendingPolicyMutationVariables = {
      icon: data.icon,
      name: String(data.name),
      description: data.description,
      companyId: company.id,
      ruleGroups: [],
    }
    const response = await createSpendingPolicy({ variables: policyData })

    const result = handleResult(response, (formErrors) => {
      onDataChanged({
        ...dataWithErrors,
        errors: formErrors,
      })
    })
    if (result.success) {
      onDataChanged({
        ...dataWithErrors,
        success: true,
        policyId: result.data?.spendingPolicy?.id,
      })
    } else {
      onDataChanged({
        ...dataWithErrors,
        success: false,
      })
    }
    return response
  }

  useOnNext(handleNext)

  return (
    <Box gap="s32" paddingTop="s40" maxWidth="s600">
      <Box gap="s8">
        <Text.Header2 weight="semibold">
          {i18n.t('spending_policies.wizard.steps.basic_information.title')}
        </Text.Header2>
        <Text.Normal>
          {i18n.t('spending_policies.wizard.steps.basic_information.description')}
        </Text.Normal>
      </Box>
      <Form padding="s0">
        <IconSelector
          defaultIcon={data.icon}
          setIcon={(value) => onDataChanged({ ...data, icon: value })}
        />
        <TextField
          mandatory
          label={i18n.t('spending_policies.details.form.name.label')}
          placeholder={i18n.t('spending_policies.details.form.name.description')}
          value={data.name}
          onChange={(value) => onDataChanged({ ...data, name: value })}
          error={data.errors?.name}
        />
        <Box gap="s8">
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('spending_policies.details.form.intro.label')}
          </Text.Normal>
          <TextEditor
            height="s200"
            placeholder={i18n.t('spending_policies.details.form.intro.description')}
            initialEditorState={{ data: data.description }}
            onChange={(value) => {
              onDataChanged({ ...data, description: String(value ?? '') })
            }}
          />
        </Box>
      </Form>
    </Box>
  )
}

export const basicInfoStep = (): WizardStep<WizardData> => ({
  title: i18n.t('spending_policies.wizard.steps.basic_information.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <BasicInfo {...props} />,
  isCompleted: () => true,
})
